import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import CopyButton from "bundles/Internal/components/common/CopyButton";
import DataTable from "bundles/Internal/components/common/DataTable";
import CandidateInterestTableFilters from "bundles/Internal/components/candidate_interest/CandidateInterestTableFilters";

// Keys that render as a clickable link → { column key: (row) => target path }
const LINK_TARGETS = {
  candidate_name: (row) => (row.candidate_id ? `/internal/candidates/${row.candidate_id}` : null),
  recruiter_name: (row) => (row.recruiter_id ? `/internal/recruiters/${row.recruiter_id}` : null),
  company_name: (row) => (row.company_id ? `/internal/companies/${row.company_id}` : null),
};

export const COLUMNS_CONSTANTS = [
  { key: "id", label: "Id" },
  { key: "candidate_name", label: "Candidate" },
  { key: "candidate_email", label: "Email" },
  { key: "candidate_phone", label: "Phone" },
  { key: "recruiter_name", label: "Recruiter" },
  { key: "company_name", label: "Company" },
  { key: "created_at", label: "Created at" },
];

const DATE_KEYS = new Set(["created_at"]);
const COPYABLE_KEYS = new Set(["candidate_email", "candidate_phone"]);

const formatValue = (value) => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat("fr-BE", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

function formatInterestCell(interest, key) {
  const value = interest?.[key];

  if (DATE_KEYS.has(key)) return formatDateTime(value);

  if (COPYABLE_KEYS.has(key)) {
    if (!value) return "";
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, minWidth: 0, maxWidth: 240 }}>
        <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, flex: 1 }}>
          {value}
        </Box>
        <CopyButton value={value} sx={{ flexShrink: 0 }} />
      </Box>
    );
  }

  return formatValue(value);
}

const CandidateInterestTable = ({
  interests = [],
  onInterestSelect,
  onQueryChange,
  filters,
  onFiltersChange,
  page,
  totalCount,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  loading = false,
  selectable = false,
  selectedRows = [],
  onRowSelect,
  onExportCsv,
}) => {
  const navigate = useNavigate();
  const columns = useMemo(() => COLUMNS_CONSTANTS, []);

  const renderCell = useCallback((interest, key) => {
    const target = LINK_TARGETS[key]?.(interest);
    const value = interest?.[key];
    if (target && value) {
      return (
        <Box
          component="span"
          onClick={(e) => {
            e.stopPropagation();
            navigate(target);
          }}
          sx={{
            color: "#079344",
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {value}
        </Box>
      );
    }
    return formatInterestCell(interest, key);
  }, [navigate]);

  return (
    <DataTable
      title="Candidate Interests"
      totalLabel={(count) => `${count.toLocaleString()} total interests`}
      emptyIcon={StarOutlineIcon}
      emptyTitle="No candidate interests found"
      emptySubtitle="Try adjusting your search criteria"
      columns={columns}
      data={interests}
      renderCell={renderCell}
      onRowClick={onInterestSelect}
      onQueryChange={onQueryChange}
      searchPlaceholder="Search by candidate, recruiter or company…"
      searchInfo="Candidate name, recruiter, company, candidate email, phone, job title"
      searchById
      filtersComponent={
        filters && onFiltersChange ? (
          <CandidateInterestTableFilters filters={filters} onFiltersChange={onFiltersChange} />
        ) : null
      }
      page={page}
      totalCount={totalCount}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
      loading={loading}
      selectable={selectable}
      selectedRows={selectedRows}
      onRowSelect={onRowSelect}
      onExportCsv={onExportCsv}
    />
  );
};

export default CandidateInterestTable;
